import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { requestPaymentRefund } from "../lib/apiPayments";

const pageWrap = {
  minHeight: "60vh",
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "center",
  padding: "16px",
};

const card = {
  width: "100%",
  maxWidth: 1120,
  background: "#fff",
  borderRadius: "16px",
  boxShadow: "0 8px 18px rgba(0,0,0,0.08)",
  padding: "24px",
};

const title = {
  fontSize: "20px",
  fontWeight: 600,
  color: "#111827",
  margin: "0 0 16px",
};

const centerCard = {
  width: "100%",
  maxWidth: 600,
  background: "#fff",
  borderRadius: "16px",
  boxShadow: "0 8px 18px rgba(0,0,0,0.08)",
  padding: "24px",
  textAlign: "center",
};

const grid = {
  display: "grid",
  gap: 16,
  gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
};

const itemCard = {
  border: "1px solid #e5e7eb",
  borderRadius: 16,
  overflow: "hidden",
};

const thumb = {
  width: "100%",
  height: 160,
  objectFit: "cover",
  background: "#f3f4f6",
};

const itemBody = { padding: 16, fontSize: 14, color: "#374151" };
const itemTitle = { fontWeight: 600, color: "#111827", margin: 0 };
const itemDesc = { margin: "6px 0 0", color: "#6b7280" };
const metaGrid = { marginTop: 12, display: "grid", gap: 4 };
const metaKey = { color: "#6b7280" };
const linksRow = { marginTop: 12, display: "flex", gap: 12, alignItems: "center" };
const actionButton = {
  border: "1px solid #d1d5db",
  background: "#fff",
  borderRadius: 999,
  padding: "8px 12px",
  fontSize: 13,
  fontWeight: 600,
  color: "#111827",
  cursor: "pointer",
};
const refundNote = {
  marginTop: 10,
  fontSize: 12,
  lineHeight: 1.45,
  color: "#6b7280",
};

// Normalize any absolute media URL to a relative one so CRA proxy handles it.
// Works for http(s)://host/media/..., bare "media/...", "./media/...", or already "/media/..."
function toRelativeMedia(url) {
  if (!url) return url;
  if (url.startsWith("data:") || url.startsWith("blob:")) return url;
  if (url.startsWith("/media/")) return url;
  if (url.startsWith("http://") || url.startsWith("https://")) {
    // strip scheme+host; keep path/query/hash
    return url.replace(/^https?:\/\/[^/]+/, "");
  }
  const trimmed = url.replace(/^\.?\/*/, ""); // remove leading "./" or "/"
  return trimmed.startsWith("media/") ? `/${trimmed}` : `/media/${trimmed}`;
}

function ArtThumb({ src, alt, style }) {
  const [state, setState] = React.useState("loading"); // loading | ok | error

  return (
    <div style={{ position: "relative" }}>
      <img
        src={src}
        alt={alt}
        style={style}
        onLoad={() => setState("ok")}
        onError={(e) => {
          console.warn("Image failed:", src, e?.nativeEvent);
          setState("error");
        }}
        referrerPolicy="no-referrer"
      />
      {state === "loading" && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#6b7280",
            fontSize: 12,
            background: "#f9fafb",
          }}
        >
          loading…
        </div>
      )}
      {state === "error" && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#b91c1c",
            fontSize: 12,
            background: "#fee2e2",
          }}
        >
          failed to load
        </div>
      )}
    </div>
  );
}

export default function MyPurchases() {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [refundMessage, setRefundMessage] = useState("");
  const [refundingSaleId, setRefundingSaleId] = useState(null);

  const loadPurchases = React.useCallback(async () => {
    const data = await api.getJson("/buyers/purchases/me?limit=50");
    setRows(data.results || []);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const data = await api.getJson("/buyers/purchases/me?limit=50");
        if (!cancelled) setRows(data.results || []);
      } catch (e) {
        if (!cancelled) setError(e.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleRequestRefund(row) {
    const paymentId = row?.payment?.id;
    if (!paymentId) return;
    const artworkTitle = row?.artwork?.title || "this artwork";
    const approverLabel = row?.payment?.refund_approver_role === "host" ? "host" : "artist";
    const refundReason = window.prompt(`Why are you requesting a refund for ${artworkTitle}?`);
    if (refundReason === null) return;
    if (!refundReason.trim()) {
      setRefundMessage("A refund reason is required.");
      return;
    }
    const confirmed = window.confirm(
      `Request a refund for ${artworkTitle}? The ${approverLabel} must approve it before Stripe processes the refund.`
    );
    if (!confirmed) return;

    setRefundMessage("");
    setRefundingSaleId(row.sale_id);
    try {
      const response = await requestPaymentRefund(paymentId, refundReason);
      await loadPurchases();
      setRefundMessage(response?.detail || "Refund request submitted successfully.");
    } catch (e) {
      setRefundMessage(e.message || "Unable to request a refund right now.");
    } finally {
      setRefundingSaleId(null);
    }
  }

  if (loading) {
    return (
      <div style={pageWrap}>
        <div style={centerCard}>
          <div style={{ fontSize: 14, color: "#4b5563" }}>Loading purchases…</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={pageWrap}>
        <div style={centerCard}>
          <div style={{ fontSize: 14, color: "#b91c1c" }}>Error: {error}</div>
        </div>
      </div>
    );
  }

  if (!rows.length) {
    return (
      <div style={pageWrap}>
        <div style={centerCard}>
          <div style={{ fontSize: 14, color: "#4b5563" }}>No purchases yet.</div>
        </div>
      </div>
    );
  }

  return (
    <div style={pageWrap}>
      <div style={card}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
          <h1 style={{ ...title, margin: 0 }}>My Purchases</h1>
          <Link
            to="/help"
            state={{ backgroundLocation: location, helpSection: "payments-help" }}
            aria-label="Help for purchases and refunds"
            style={{
              width: 36,
              height: 36,
              borderRadius: "999px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              textDecoration: "none",
              fontSize: 18,
              fontWeight: 800,
              color: "#111827",
              background: "rgba(255,255,255,0.82)",
              boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
            }}
          >
            ?
          </Link>
        </div>
        {refundMessage && (
          <div
            style={{
              marginBottom: 16,
              padding: "12px 14px",
              borderRadius: 12,
              background: refundMessage.toLowerCase().includes("unable") || refundMessage.toLowerCase().includes("error")
                ? "#fee2e2"
                : "#ecfdf5",
              color: refundMessage.toLowerCase().includes("unable") || refundMessage.toLowerCase().includes("error")
                ? "#991b1b"
                : "#065f46",
              fontSize: 14,
            }}
          >
            {refundMessage}
          </div>
        )}
        <div style={grid}>
          {rows.map((r) => {
            const art = r.artwork || {};
            const link = art?.id ? `/artworks/${art.id}` : undefined;

            const rawSrc =
              art.image_url ??
              art.image ??
              (Array.isArray(art.images) ? art.images[0]?.image : null);

            // ✅ normalize to relative so it works via the tunnel
            const imgSrc = toRelativeMedia(rawSrc);

            return (
              <div key={r.sale_id} style={itemCard}>
                {imgSrc ? (
                  link ? (
                    <Link to={link}>
                      <ArtThumb src={imgSrc} alt={art.title} style={thumb} />
                    </Link>
                  ) : (
                    <ArtThumb src={imgSrc} alt={art.title} style={thumb} />
                  )
                ) : (
                  <div style={thumb} />
                )}

                <div style={itemBody}>
                  <div style={itemTitle}>
                    {link ? (
                      <Link to={link} style={{ color: "#111827", textDecoration: "underline" }}>
                        {art.title || "Untitled"}
                      </Link>
                    ) : (
                      art.title || "Untitled"
                    )}
                  </div>

                  {art.description && (
                    <p style={itemDesc}>
                      {art.description.length > 160
                        ? art.description.slice(0, 157) + "…"
                        : art.description}
                    </p>
                  )}

                  <div style={metaGrid}>
                    <div>
                      <span style={metaKey}>Date: </span>
                      {r.sale_date?.slice(0, 10) || "—"}
                    </div>
                    <div>
                      <span style={metaKey}>Price: </span>
                      {r.price ?? "—"}
                    </div>
                    <div>
                      <span style={metaKey}>Status: </span>
                      {r.payment?.status || "—"}
                    </div>
                  </div>

                  <div style={linksRow}>
                    {r.payment?.receipt_url && (
                      <a
                        href={r.payment.receipt_url}
                        target="_blank"
                        rel="noreferrer"
                        style={{ textDecoration: "underline" }}
                      >
                        View receipt
                      </a>
                    )}
                    {link && (
                      <Link to={link} style={{ textDecoration: "underline" }}>
                        View artwork
                      </Link>
                    )}
                    {r.payment?.refundable && (
                      <button
                        type="button"
                        style={{
                          ...actionButton,
                          opacity: refundingSaleId === r.sale_id ? 0.7 : 1,
                          cursor: refundingSaleId === r.sale_id ? "wait" : "pointer",
                        }}
                        onClick={() => handleRequestRefund(r)}
                        disabled={refundingSaleId === r.sale_id}
                      >
                        {refundingSaleId === r.sale_id ? "Requesting refund..." : "Request refund"}
                      </button>
                    )}
                  </div>
                  {r.payment?.status === "refund_pending" && (
                    <div style={refundNote}>
                      Stripe is processing this refund request. The payment status will switch to refunded once that confirmation arrives.
                    </div>
                  )}
                  {r.payment?.status === "refund_requested" && (
                    <div style={refundNote}>
                      This refund request is awaiting seller approval before Stripe is asked to process it.
                      {r.payment?.refund_request_reason ? ` Reason: ${r.payment.refund_request_reason}` : ""}
                    </div>
                  )}
                  {r.payment?.status === "refund_rejected" && (
                    <div style={{ ...refundNote, color: "#991b1b" }}>
                      This refund request was rejected by the seller.
                      {r.payment?.refund_rejection_reason ? ` Reason: ${r.payment.refund_rejection_reason}` : ""}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
